'use client';

import { createContext, PropsWithChildren, useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { Subject } from 'rxjs';
import { DriftWindow } from '../../window/driftWindow';
import { EventLoopMetricsTracker } from './EventLoopMetricsTracker';
import { METRICS_SAMPLING_LOGGING_ENABLED } from './metrics';
import { MetricsTracker } from './MetricsTracker';
import { MetricsEventBusMessage } from './metricsTypes';
import { NetworkMetricsTracker } from './NetworkMetricsTracker';
import { SlotSubscriberMetricsTracker } from './SlotSubscriberMetricsTracker';
import { needsDataRequirement } from 'src/utils/routeDataRequirements';
import { slotSubject } from 'src/utils/slotMetrics';

const EVENT_BUS = new Subject<MetricsEventBusMessage>();

export const MetricsContext = createContext({
	eventBus: EVENT_BUS,
});

const MetricsProvider = (props: PropsWithChildren<any>) => {
	const pathname = usePathname();
	const returnValue = useMemo(() => {
		return { eventBus: EVENT_BUS };
	}, []);

	// Check if current route needs detailed metrics tracking
	// Enable metrics on trading, portfolio, and stats pages where performance matters most
	const needsMetrics = pathname
		? needsDataRequirement(pathname, 'trading') ||
			needsDataRequirement(pathname, 'portfolio') ||
			needsDataRequirement(pathname, 'stats')
		: false;

	useEffect(() => {
		window.UNCONSUMED_GRAFANA_METRICS = {};
		DriftWindow.initialiseMetricsBus(EVENT_BUS);
	}, []);

	useEffect(() => {
		EVENT_BUS.subscribe((message) => {
			switch (message.type) {
				case 'event_loop_lag':
					window.UNCONSUMED_GRAFANA_METRICS.EVENT_LOOP_LAG = [
						...(window.UNCONSUMED_GRAFANA_METRICS.EVENT_LOOP_LAG ?? []),
						message.value ?? 0,
					];
					break;
				default:
					break;
			}
		});
	}, []);

	/**
	 * This hook sets up the metrics which should only be enabled if the session has been selected as a sample session
	 * and if the current route needs detailed performance tracking
	 */
	useEffect(() => {
		if (!METRICS_SAMPLING_LOGGING_ENABLED || !needsMetrics) {
			return;
		}

		const metricsTracker = new MetricsTracker();

		// # Network Metrics
		const networkMetricsAlarmTracker = new NetworkMetricsTracker(
			metricsTracker.metricsEventSink
		);
		networkMetricsAlarmTracker.subscribe();

		// # Slot Metrics
		const slotMetricsAlarmTracker = new SlotSubscriberMetricsTracker(
			slotSubject
		);
		slotMetricsAlarmTracker.subscribe();

		// # Event Loop Blocking Metrics
		const eventLoopMetricsTracker = new EventLoopMetricsTracker((lag) => {
			EVENT_BUS.next({
				type: 'event_loop_lag',
				value: lag,
			});
		});
		eventLoopMetricsTracker.subscribe();

		return () => {
			// Cleanup metrics trackers properly to prevent memory leaks
			eventLoopMetricsTracker.unsubscribe();
			// Note: NetworkMetricsTracker and SlotSubscriberMetricsTracker create RxJS subscriptions
			// but don't expose unsubscribe methods. This is a potential memory leak that should be addressed.
		};
	}, [needsMetrics]);

	return <MetricsContext value={returnValue}>{props.children}</MetricsContext>;
};

export default MetricsProvider;
